import React, { memo } from 'react';
import { graphql, useFragment } from 'react-relay';
import {
	JIRA_PROJECT_TYPE_SOFTWARE_PROJECT,
	COMPANY_MANAGED_PROJECT,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import type { NavHeader_query$key } from '@atlassian/jira-relay/src/__generated__/NavHeader_query.graphql';
import type { NavHeader_project$key } from '@atlassian/jira-relay/src/__generated__/NavHeader_project.graphql';
import { HorizontalNavBoardHeader } from './board-header/HorizontalNavBoardHeader.tsx';
import { ProjectHeader } from './project-header/ProjectHeader.tsx';
import { UserBoardHeader } from './user-board-header/UserBoardHeader.tsx';

type Props = {
	queryFragment: NavHeader_query$key;
	projectFragment?: NavHeader_project$key | null;
};

const NavHeader = ({ queryFragment, projectFragment = null }: Props): React.JSX.Element | null => {
	const data = useFragment(
		graphql`
			fragment NavHeader_query on Query {
				...ProjectHeader_query
				jira @required(action: THROW) {
					jiraBoard(id: $boardId) @include(if: $withBoard) {
						__typename
						... on JiraBoard {
							...UserBoardHeader_board
							...HorizontalNavBoardHeader_board
						}
					}
				}
			}
		`,
		queryFragment,
	);

	const projectData = useFragment(
		graphql`
			fragment NavHeader_project on JiraProject {
				...ProjectHeader_project
				...HorizontalNavBoardHeader_project
				projectType @required(action: THROW)
				projectStyle @required(action: THROW)
				softwareBoardCount
				mostRecentlyViewedBoard @skip(if: $isTMPBoardRoute) {
					... on JiraBoard {
						...HorizontalNavBoardHeader_board
					}
				}
			}
		`,
		projectFragment,
	);

	// a user board can only exist outside of a project
	if (data.jira.jiraBoard?.__typename === 'JiraBoard' && !projectData) {
		return <UserBoardHeader boardFragment={data.jira.jiraBoard} />;
	}

	// from this point, we only support project scope
	if (!projectData) {
		return null;
	}

	const board = data.jira.jiraBoard ?? projectData?.mostRecentlyViewedBoard;

	// softwareBoardCount >= 1 condition ensures boardless projects have the correct header (ie ProjectHeader)
	if (
		board &&
		projectData.projectType === JIRA_PROJECT_TYPE_SOFTWARE_PROJECT &&
		projectData.projectStyle === COMPANY_MANAGED_PROJECT &&
		projectData.softwareBoardCount &&
		projectData.softwareBoardCount >= 1
	) {
		// @ts-expect-error Expecting board to be a JiraBoard type
		return <HorizontalNavBoardHeader boardFragment={board} projectFragment={projectData} />;
	}

	return <ProjectHeader queryFragment={data} projectFragment={projectData} />;
};

export default memo(NavHeader);
